import styled from '@emotion/styled';
import { useDrag } from '@use-gesture/react';
import { clamp } from 'lodash';
import { type FC, useEffect, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import ConnectedPlayerSidebar from './Sidebar/ConnectedPlayerSidebar';

export const SIDEBAR_WIDTH_BREAKPOINT = 750;

const SidebarContainer = styled(animated.div)`
  top: 0;
  left: 0;
  height: 100%;
  background: white;
  z-index: 300;
  overflow: hidden;
  border-right: 1px solid var(--base-color-dark);
  transform: translateZ(0);

  @media screen and (max-width: 750px) {
    position: absolute;
    top: 0;
    left: 0;
    right: 50px;
    bottom: 0;
    background: white;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border-right: none;
    z-index: 1000;
  }
`;

const SidebarBackdrop = styled(animated.div)`
  @media screen and (min-width: 751px) {
    display: none;
  }

  @media screen and (max-width: 750px) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    touch-action: none;
    backdrop-filter: brightness(1);
    -webkit-backdrop-filter: brightness(1);
  }
`;

interface Props {
  closed: boolean;
  onClose: () => void;
}

export const PlayerSidebarWrapper: FC<Props> = ({ closed, onClose }) => {
  const [showContent, setShowContent] = useState(!closed);
  const windowWidth = window.innerWidth;
  const sidebarWidth =
    windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? clamp(windowWidth * 0.3, 300, 400) : windowWidth - 50;
  const [{ x }, api] = useSpring(() => ({
    x: closed ? -sidebarWidth : 0,
    onRest: (result) => {
      setShowContent(result.value.x > -sidebarWidth);
    },
  }));

  useEffect(() => {
    if (closed) {
      api.start({ x: -sidebarWidth });
    } else {
      api.start({ x: 0 });
      setShowContent(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed]);

  const bind = useDrag(({ down, last, tap, movement: [mx], event }) => {
    event.preventDefault();
    if (tap) {
      onClose();
      return;
    }
    if (last) {
      if (mx < -sidebarWidth / 2) {
        onClose();
        return;
      }
    }
    api.start({ x: down ? Math.min(mx, 0) : 0, immediate: down });
  }, {});

  return (
    <>
      <SidebarContainer
        className="side-bar-container"
        style={{
          width: windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? x.to((x) => sidebarWidth + x) : undefined,
          x: windowWidth > SIDEBAR_WIDTH_BREAKPOINT ? undefined : x,
        }}
        onTransitionEnd={() => {
          setShowContent(!closed);
        }}
      >
        {showContent && (
          <div style={{ width: sidebarWidth, height: '100%' }}>
            <ConnectedPlayerSidebar onClose={onClose} />
          </div>
        )}
      </SidebarContainer>
      <SidebarBackdrop
        className="side-bar-backdrop"
        style={{
          pointerEvents: closed ? 'none' : 'auto',
          backdropFilter: x.to(
            (x) =>
              `brightness(${x === -sidebarWidth ? 1 : Math.min(1, -0.7 * (x / sidebarWidth) + 0.3)})`,
          ),
          WebkitBackdropFilter: x.to(
            (x) =>
              `brightness(${x === -sidebarWidth ? 1 : Math.min(1, -0.7 * (x / sidebarWidth) + 0.3)})`,
          ),
        }}
        {...bind()}
      />
    </>
  );
};
